/**
 * RecurrenceListController — page /factures-recurrentes-liste.html
 * (EPIC-014.4).
 *
 * Liste paginée des facturations récurrentes de l'org. Filtres statut,
 * recherche client (libre `q`), périodicité (`intervalMonths`).
 * Pagination 25/page. Synchronisation des paramètres avec la query
 * string de l'URL pour permettre le partage / retour navigateur.
 *
 * Action contextuelle par ligne : lien « Voir » vers
 * `/factures-recurrence-detail.html?id={id}`.
 *
 * Aucun HTML inline en JS — toutes les lignes sont construites via
 * `<template id="tpl-recurrence-row">` + `cloneTemplate()` + `fillSlots()`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const STATUS_LABELS = {
  active: "active",
  paused: "en pause",
  ended: "terminée",
  archived: "archivée",
};

const STATUS_PILL_MODIFIER = {
  active: "nm-pill--olive",
  paused: "nm-pill--ochre",
  ended: "",
  archived: "",
};

export default class RecurrenceListController extends BaseController {
  static targets = ["summary", "rows", "pagination", "count"];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    q: { type: String, default: "" },
    statusFilter: { type: String, default: "" },
    intervalFilter: { type: String, default: "" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._restoreFromQueryString();
    this._bindFilterInputs();
    this.refresh();
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  refresh(event) {
    if (event) {
      this._readFilterInputs();
      this.pageValue = 1;
    }
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._fetch(), 200);
  }

  goToPage(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (Number.isFinite(page) && page >= 1) {
      this.pageValue = page;
      this._fetch();
    }
  }

  async _fetch() {
    const seq = ++this._fetchSeq;
    this._syncQueryString();

    const params = {
      page: this.pageValue,
      perPage: this.perPageValue,
    };
    if (this.qValue) params["filter[q]"] = this.qValue;
    if (this.statusFilterValue) params["filter[status]"] = this.statusFilterValue;
    if (this.intervalFilterValue) params["filter[intervalMonths]"] = this.intervalFilterValue;

    try {
      const response = await api.get("/recurring-invoices", params);
      if (seq !== this._fetchSeq) return;
      this._renderRows(response.items || []);
      this._renderPagination(response.pagination || {});
      this._renderSummary(response.summary || null);
    } catch (err) {
      if (seq !== this._fetchSeq) return;
      const message = err instanceof APIError ? err.message : "Erreur de chargement.";
      this.showError(message);
      if (this.hasRowsTarget) this.rowsTarget.replaceChildren();
      if (this.hasSummaryTarget) this.summaryTarget.textContent = "—";
    }
  }

  _renderRows(items) {
    if (!this.hasRowsTarget) return;
    this.rowsTarget.replaceChildren();

    if (items.length === 0) {
      const empty = document.createElement("tr");
      const cell = document.createElement("td");
      cell.colSpan = 8;
      cell.className = "muted fs-13";
      cell.style.padding = "24px 0";
      cell.style.textAlign = "center";
      cell.textContent = "Aucune récurrence pour ces filtres.";
      empty.appendChild(cell);
      this.rowsTarget.appendChild(empty);
      return;
    }

    for (const item of items) {
      const fragment = this.cloneTemplate("tpl-recurrence-row");
      if (!fragment) continue;
      const status = item.status || "active";
      const intervalLabel = item.intervalMonths === 1
        ? "tous les mois"
        : `tous les ${item.intervalMonths} mois`;
      this.fillSlots(fragment, {
        "client-name": item.clientName || "—",
        "interval-label": intervalLabel,
        "day-of-month": String(item.dayOfMonth),
        "start-date": this.formatShortDate(item.startDate) || "—",
        "next-generation-date": this.formatShortDate(item.nextGenerationDate) || "—",
        "status-label": STATUS_LABELS[status] || status,
      });

      const detailUrl = `/factures-recurrence-detail.html?id=${encodeURIComponent(item.id)}`;
      fragment.querySelectorAll('[data-href="detail"]').forEach((node) => {
        node.setAttribute("href", detailUrl);
      });

      const pill = fragment.querySelector('[data-slot-class="status-pill"]');
      if (pill) {
        const modifier = STATUS_PILL_MODIFIER[status] || "";
        pill.className = `nm-pill ${modifier}`.trim();
      }

      this.rowsTarget.appendChild(fragment);
    }
  }

  _renderPagination(pagination) {
    if (!this.hasCountTarget) return;
    const total = pagination.total ?? 0;
    const page = pagination.page ?? this.pageValue;
    const perPage = pagination.perPage ?? this.perPageValue;
    const start = total === 0 ? 0 : (page - 1) * perPage + 1;
    const end = Math.min(total, page * perPage);
    this.countTarget.textContent = total === 0
      ? "Aucune récurrence"
      : `${start} — ${end} sur ${total} récurrence${total > 1 ? "s" : ""}`;
  }

  // Sous-titre d'en-tête (99.83) — agrégats globaux de l'organization renvoyés
  // dans `response.summary` (activeCount, pausedCount, nextGenerationDate).
  _renderSummary(summary) {
    if (!this.hasSummaryTarget) return;
    if (!summary) {
      this.summaryTarget.textContent = "—";
      return;
    }
    const active = Number(summary.activeCount) || 0;
    const paused = Number(summary.pausedCount) || 0;
    if (active === 0 && paused === 0) {
      this.summaryTarget.textContent = "Aucune récurrence configurée.";
      return;
    }
    const parts = [
      `${active} récurrence${active > 1 ? "s actives" : " active"}`,
      `${paused} en pause`,
    ];
    if (summary.nextGenerationDate) {
      const date = new Intl.DateTimeFormat("fr-FR", {
        day: "numeric", month: "long", year: "numeric",
      }).format(new Date(summary.nextGenerationDate));
      parts.push(`prochaine génération le ${date}`);
    }
    this.summaryTarget.textContent = parts.join(" · ");
  }

  _bindFilterInputs() {
    const root = this.element;
    this._statusFilter = root.querySelector('select[data-action*="recurrence-list#refresh"]:nth-of-type(1)');
    this._intervalFilter = root.querySelector('select[data-action*="recurrence-list#refresh"]:nth-of-type(2)');
    this._qInput = root.querySelector('input[data-action*="recurrence-list#refresh"]');
  }

  _readFilterInputs() {
    if (this._statusFilter) this.statusFilterValue = this._statusFilter.value || "";
    if (this._intervalFilter) this.intervalFilterValue = this._intervalFilter.value || "";
    if (this._qInput) this.qValue = (this._qInput.value || "").trim();
  }

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get("page"));
    if (Number.isFinite(page) && page >= 1) this.pageValue = page;
    const q = params.get("q");
    if (q) this.qValue = q;
    const status = params.get("status");
    if (status) this.statusFilterValue = status;
    const interval = params.get("interval");
    if (interval) this.intervalFilterValue = interval;
  }

  _syncQueryString() {
    const params = new URLSearchParams();
    if (this.pageValue > 1) params.set("page", String(this.pageValue));
    if (this.qValue) params.set("q", this.qValue);
    if (this.statusFilterValue) params.set("status", this.statusFilterValue);
    if (this.intervalFilterValue) params.set("interval", this.intervalFilterValue);
    const qs = params.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState({}, "", url);
  }
}
